import { doc, getFirestore, updateDoc } from "firebase/firestore";
import { useCallback, useState, type ChangeEvent } from "react";
import { toast } from "react-toastify";
import type {
  AdminFieldKey,
  ModalEditAdminProps,
  SelectedAdmin,
  UseModalEditAdminReturn,
} from "./types";

type AdminDraft = {
  cantidadRutas: number;
  email: string;
  nombre: string;
};

const normalizarRutas = (value: number | string | null | undefined): number => {
  const rutas = Number(value ?? 0);

  return Number.isNaN(rutas) ? 0 : rutas;
};

const normalizarAdminDraft = (
  email: string,
  nombre: string,
  rutas: number | string
): AdminDraft => ({
  cantidadRutas: normalizarRutas(rutas),
  email: email.trim(),
  nombre: nombre.trim(),
});

const obtenerAdminBase = (selectedAdmin: SelectedAdmin): AdminDraft =>
  normalizarAdminDraft(
    selectedAdmin.email ?? "",
    selectedAdmin.nombre,
    selectedAdmin.cantidadRutas ?? 0
  );

const validarAdminDraft = (draft: AdminDraft): string | null => {
  if (!draft.email || !draft.nombre) {
    return "Completa todos los campos";
  }

  if (draft.cantidadRutas < 0) {
    return "La cantidad de rutas no puede ser negativa";
  }

  return null;
};

const adminDraftCambio = (draft: AdminDraft, selectedAdmin: SelectedAdmin): boolean => {
  const adminBase = obtenerAdminBase(selectedAdmin);

  return (
    draft.email !== adminBase.email ||
    draft.nombre !== adminBase.nombre ||
    draft.cantidadRutas !== adminBase.cantidadRutas
  );
};

export const useModalEditAdmin = ({
  selectedAdmin,
  setModalEdit,
  setUsersAdminData,
}: ModalEditAdminProps): UseModalEditAdminReturn | null => {
  const [email, setEmail] = useState(selectedAdmin?.email ?? "");
  const [nombre, setNombre] = useState(selectedAdmin?.nombre ?? "");
  const [rutas, setRutas] = useState(selectedAdmin?.cantidadRutas ?? "");
  const [isEditing, setIsEditing] = useState(false);

  const handleClose = useCallback(() => {
    setModalEdit(false);
  }, [setModalEdit]);

  const handleFieldChange = useCallback(
    (field: AdminFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (field === "email") {
        setEmail(value);
        return;
      }

      if (field === "nombre") {
        setNombre(value);
        return;
      }

      setRutas(value);
    },
    []
  );

  const editarAdmin = useCallback(async () => {
    if (!selectedAdmin || isEditing) {
      return;
    }

    const adminDraft = normalizarAdminDraft(email, nombre, rutas);
    const errorValidacion = validarAdminDraft(adminDraft);

    if (errorValidacion) {
      toast.error(errorValidacion);
      return;
    }

    if (!adminDraftCambio(adminDraft, selectedAdmin)) {
      handleClose();
      return;
    }

    setIsEditing(true);

    try {
      const db = getFirestore();
      const adminRef = doc(db, "admin_users", selectedAdmin.uid);

      await updateDoc(adminRef, {
        cantidadRutas: adminDraft.cantidadRutas,
        email: adminDraft.email,
        nombre: adminDraft.nombre,
      });

      setUsersAdminData((prevAdmins) =>
        prevAdmins
          ? prevAdmins.map((admin) =>
              admin.uid === selectedAdmin.uid
                ? {
                    ...admin,
                    cantidadRutas: adminDraft.cantidadRutas,
                    email: adminDraft.email,
                    nombre: adminDraft.nombre,
                  }
                : admin
            )
          : prevAdmins
      );

      handleClose();
      toast.success("Admin editado con exito");
    } catch (error) {
      console.log("Error al editar admin", error);
      toast.error("No se pudo editar el administrador");
    } finally {
      setIsEditing(false);
    }
  }, [email, handleClose, isEditing, nombre, rutas, selectedAdmin, setUsersAdminData]);

  if (!selectedAdmin) {
    return null;
  }

  return {
    editarAdmin,
    email,
    handleClose,
    handleFieldChange,
    isEditing,
    nombre,
    rutas,
    selectedAdmin,
  };
};
